import { useState } from 'react';
import { Drawer, notification } from 'antd';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { startCase } from 'lodash';

import IntlMessages from 'util/IntlMessages';
import { errorNotification } from 'appRedux/actions';
import CustomInput from 'components/CustomInput';
import AddressMapPicker from 'components/AddressMapPicker';
import BoxContainer from 'components/BoxContainer';
import FilterContainer from 'components/FilterContainer';
import Title from 'components/BoxContainer/components/Title';
import { organizationSaveFetch } from 'appRedux/actions';

import styles from './styles.module.less';

const EMPTY_ADDRESS = {
  address: '',
  administrative_area_level_1: '',
  administrative_area_level_2: '',
  country: '',
  latitude: '',
  locality: '',
  longitude: '',
  postal_code: '',
  route: '',
  street_number: '',
  sublocality_level_1: '',
};

const DEFAULT_MAP_POSITION = { lat: -33.4489, lon: -70.6693 };

const NewOrganizationModal = ({ closeModal, isOpen }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [address, setAddress] = useState(null);
  const sizeSteps = ['1-10', '11-50', '51-200', '201-500', '501-1000', '+1000'];

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    setValue,
    formState: { dirtyFields, isValid, errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      size: 0,
      name: '',
      address: '',
      phone: '',
    },
  });

  const onEnter = f => e => {
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      e.preventDefault();
      f();
    }
  };

  const handleAddressSelect = value => {
    const normalizedAddress = {
      ...EMPTY_ADDRESS,
      administrative_area_level_1: value?.state || value?.region || '',
      administrative_area_level_2: value?.county || '',
      country: value?.country || '',
      latitude: value?.latitude ?? '',
      locality: value?.city || value?.town || value?.village || '',
      longitude: value?.longitude ?? '',
      postal_code: value?.postcode || '',
      route: value?.road || value?.route || '',
      street_number: value?.house_number || value?.streetNumber || '',
      sublocality_level_1: value?.suburb || value?.neighbourhood || '',
    };

    const fullAddress = [normalizedAddress.route, normalizedAddress.street_number]
      .filter(Boolean)
      .join(' ')
      .trim();

    normalizedAddress.address = fullAddress;

    setAddress(normalizedAddress);
    setValue('address', fullAddress, { shouldDirty: true, shouldValidate: true });
  };

  const customSubmit = values => {
    const isDirty = Object.keys(dirtyFields).length !== 0;

    if (isDirty && !address) {
      dispatch(errorNotification(intl.formatMessage({ id: 'form.selectAddress' })));
      return;
    }

    // Rules treat phone as optional; omit null so create does not fail maxLength checks.
    const phone = values.phone == null || values.phone === '' ? undefined : values.phone;
    const sizeIndex = Number(values.size);
    const fullValues = {
      ...(phone !== undefined ? { phone } : {}),
      address: {
        ...EMPTY_ADDRESS,
        ...(address || {}),
        address: values.address || '',
      },
      size: sizeSteps[Number.isFinite(sizeIndex) ? sizeIndex : 0] || sizeSteps[0],
      name: startCase(values.name.trim().toLowerCase()),
    };

    if (isDirty) {
      dispatch(organizationSaveFetch(null, fullValues));
      const message = intl.formatMessage({ id: 'general.save.successful.message' });
      notification.success({ message });
      reset({
        size: 0,
        name: '',
        address: '',
        phone: '',
      });
      setAddress(null);
      closeModal();
    }
  };

  const formSubmit = handleSubmit(customSubmit);

  return (
    <Drawer
      className={styles.newOrganizationDrawer}
      placement="right"
      open={isOpen}
      onClose={closeModal}
      destroyOnClose
      title={null}
      closable={false}
      bodyStyle={{ padding: 0 }}
    >
      <BoxContainer>
        <BoxContainer fullContent>
          <FilterContainer
            goBack={closeModal}
            title={<Title.Header value={intl.formatMessage({ id: 'sidebar.newOrganization' })} />}
            buttonItems={[
              {
                iconName: 'save',
                action: formSubmit,
                disabled: !isValid,
              },
            ]}
          />
        </BoxContainer>

        <BoxContainer fullContent>
          <form onSubmit={formSubmit}>
            <CustomInput
              autoFocus
              onKeyDown={onEnter(() => setFocus('phone'))}
              label={intl.formatMessage({ id: 'onboarding.stepTwo.organizationName' })}
              errors={errors}
              register={register('name', {
                required: intl.formatMessage({ id: 'form.required.msg' }),
                maxLength: {
                  value: 255,
                  message: intl.formatMessage({ id: 'form.maxLength.msg' }, { amount: 255 }),
                },
                minLength: {
                  value: 3,
                  message: intl.formatMessage({ id: 'form.minLength.msg' }, { amount: 3 }),
                },
              })}
            />
            <CustomInput
              onKeyDown={onEnter(() => setFocus('size'))}
              className="m-top-5"
              label={intl.formatMessage({ id: 'onboarding.stepTwo.organizationPhone' })}
              errors={errors}
              register={register('phone', {
                pattern: {
                  value: /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/,
                  message: intl.formatMessage({
                    id: 'onboarding.stepTwo.organizationPhone.pattern.error',
                  }),
                },
                maxLength: {
                  value: 50,
                  message: intl.formatMessage({ id: 'form.maxLength.msg' }, { amount: 50 }),
                },
              })}
            />
            <div className="m-top-5 gx-mb-2">
              <label className="m-bottom-2" htmlFor="size">
                <IntlMessages id="onboarding.stepTwo.organizationSize" />
              </label>
              <input
                className="gx-w-100"
                type="range"
                list="tickmarks"
                defaultValue={0}
                {...register('size')}
                max={sizeSteps.length - 1}
              />
              <datalist id="tickmarks" className={styles.slider}>
                {sizeSteps.map((v, i) => (
                  <option key={v} value={i} label={v} />
                ))}
              </datalist>
            </div>

            <div className="m-top-5">
              <label className="m-bottom-2" htmlFor="address">
                <IntlMessages id="onboarding.stepTwo.address" />
              </label>
              <AddressMapPicker
                customHeight="250px"
                visible={isOpen}
                includeMyLocation
                initialPosition={
                  address?.latitude && address?.longitude
                    ? { lat: Number(address.latitude), lon: Number(address.longitude) }
                    : DEFAULT_MAP_POSITION
                }
                onAddressSelect={handleAddressSelect}
              />
            </div>
          </form>
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

export default NewOrganizationModal;
